/**
 * Mettre en texte la valeur d'un attribut Merchant.
 *
 * ## Pourquoi ces fonctions vivent dans leur propre fichier
 *
 * Elles étaient au sommet de `FeedOptimizerTool.jsx`, et **le même défaut s'y est produit
 * deux fois** : une valeur qui n'est pas une chaîne, posée telle quelle dans le JSX, fait
 * tomber l'écran entier — « Objects are not valid as a React child ».
 *
 * 1. le **prix**, qui arrive en `{ amountMicros, currencyCode }` ;
 * 2. les **caractéristiques techniques**, qui arrivent en tableau de
 *    `{ sectionName, attributeName, attributeValue }`.
 *
 * La seconde fois, le crash est venu d'un endroit que la première correction n'avait pas
 * touché — la liste de puces de l'onglet Suggestions, qui faisait `{key}: {value}`. Il y
 * dormait depuis l'arrivée de `product_highlight` : React rend un tableau de **chaînes** sans
 * broncher, si bien que le code paraissait juste jusqu'à ce qu'un tableau d'**objets** passe.
 *
 * Dans un `.jsx`, rien de tout cela n'est vérifiable hors ligne : le projet teste au script
 * Node, et Node ne lit pas le JSX. Ici, si — et `scripts/test-texte-attribut.js` exerce
 * chaque forme que `suggestedAttributes` peut porter.
 *
 * ## La règle
 *
 * **La fonction est totale.** Toute entrée rend une chaîne ; une forme imprévue se rend
 * brute plutôt que d'interrompre l'écran. C'est une fiche produit, pas un endroit où une
 * forme inconnue doit coûter la page.
 */

/** Les états de stock de Merchant, en français. Clés normalisées par `normaliserEnum`. */
const LIBELLES_DISPONIBILITE = {
  in_stock: 'En stock',
  out_of_stock: 'En rupture de stock',
  preorder: 'En précommande',
  backorder: 'En réapprovisionnement',
  limited_availability: 'Disponibilité limitée'
};

/** Une adresse http(s) : on peut l'ouvrir, donc en faire un lien. */
const estAdresse = (valeur) => typeof valeur === 'string' && /^https?:\/\//i.test(valeur);

/**
 * Le nom de fichier d'une adresse.
 *
 * Les cinq images supplémentaires d'un produit partagent cent caractères de préfixe et ne
 * diffèrent que par la fin : affichées entières, elles font un mur où rien ne se distingue ;
 * tronquées par la gauche, elles perdent justement ce qui les sépare. Le nom de fichier est
 * court, il distingue, et l'infobulle garde l'adresse complète.
 */
const nomDeFichier = (adresse) => {
  try {
    const segments = new URL(adresse).pathname.split('/').filter(Boolean);
    return decodeURIComponent(segments.pop() || adresse);
  } catch {
    // Une adresse que `URL` refuse reste lisible telle quelle.
    return adresse;
  }
};

/** `IN_STOCK`, `in stock`, `in-stock` désignent le même état selon la version de l'API. */
const normaliserEnum = (valeur) => String(valeur).trim().toLowerCase().replace(/[\s-]+/g, '_');

/**
 * Un prix Merchant, en clair.
 *
 * L'API v1 le rend sous la forme `{ amountMicros: '24900000', currencyCode: 'EUR' }` — des
 * micro-unités, et une devise qui n'est pas toujours l'euro.
 */
const formaterPrix = ({ amountMicros, currencyCode }) => {
  const montant = Number(amountMicros) / 1000000;
  if (!Number.isFinite(montant)) return null;

  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode || 'EUR'
    }).format(montant);
  } catch {
    // `Intl` refuse un code devise qu'il ne connaît pas : mieux vaut un prix sans symbole
    // qu'une fiche qui ne s'affiche plus.
    return `${montant.toFixed(2)} ${currencyCode || ''}`.trim();
  }
};

/**
 * Les unités de quantité vendue admises par Google.
 *
 * ⚠️ **Jumelles de `backend/src/lib/mesureUnitaire.js` et du catalogue**, et les trois doivent
 * rester d'accord — même parti que `vignetteDe` et `memeMarque`, pour la même raison : l'écran
 * ne peut pas charger un module CommonJS du serveur, et une validation côté serveur imposerait
 * un aller-retour par caractère tapé. `backend/scripts/test-mesure-unitaire.js` confronte les
 * trois listes.
 */
const UNITES_MESURE = [
  'mg', 'g', 'kg', 'ml', 'cl', 'l', 'oz', 'lb', 'floz', 'pt', 'qt', 'gal',
  'in', 'ft', 'yd', 'cm', 'm', 'sqft', 'sqm', 'ct'
];

/** « 250 ml » → `{ value: 250, unit: 'ml' }`. `null` si ça ne se lit pas. */
function lireMesureSaisie(texte) {
  const brut = String(texte || '').trim();
  if (!brut) return null;
  const m = brut.match(new RegExp(`^(\\d+(?:[.,]\\d+)?)\\s*(${UNITES_MESURE.join('|')})$`, 'i'));
  if (!m) return null;
  const value = parseFloat(m[1].replace(',', '.'));
  if (!Number.isFinite(value) || value <= 0) return null;
  return { value: Math.round(value * 1e6) / 1e6, unit: m[2].toLowerCase() };
}

/** `{ value: 250, unit: 'ml' }` → « 250 ml ». Chaîne vide si la forme ne tient pas. */
function texteMesure(mesure) {
  if (!mesure || typeof mesure !== 'object') return '';
  const v = Number(mesure.value);
  const u = String(mesure.unit || '').toLowerCase();
  if (!Number.isFinite(v) || v <= 0 || !UNITES_MESURE.includes(u)) return '';
  return `${Number.isInteger(v) ? v : Math.round(v * 1e6) / 1e6} ${u}`;
}

/**
 * La valeur d'un attribut, telle qu'on peut l'écrire dans la fiche.
 *
 * ⚠️ **Tous les attributs ne sont pas des chaînes.** Le prix arrive en objet, et le poser tel
 * quel dans le JSX fait tomber tout l'écran : « Objects are not valid as a React child (found:
 * object with keys {amountMicros, currencyCode}) ». Le défaut dormait depuis l'origine — il n'a
 * pu se manifester qu'après la première analyse postérieure à la table d'attributs (`fe839ea`),
 * puisque la projection d'avant ne gardait pas le prix et que la carte affichait un vide.
 *
 * **La fonction est totale.** Un type inattendu se rend en texte plutôt que d'interrompre
 * l'écran : c'est une fiche produit, pas un endroit où une forme inconnue doit coûter la page.
 */
const texteAttribut = (valeur, nom) => {
  if (valeur === null || valeur === undefined || valeur === '') return '';

  if (Array.isArray(valeur)) {
    return valeur.map(element => texteAttribut(element)).filter(Boolean).join(', ');
  }

  if (typeof valeur === 'object') {
    if ('amountMicros' in valeur) return formaterPrix(valeur) || '';
    /*
      Une caractéristique technique est un triplet : section, nom, valeur.

      Reconnue sur sa forme, comme le prix juste au-dessus — cette fonction n'a pas accès à la
      table d'attributs, et c'est très bien ainsi : elle sert aussi à rendre les **éléments**
      d'une liste, où le nom de l'attribut est déjà perdu.

      Sans ce cas, le garde-fou plus bas rendait `{"attributeName":"Hauteur",…}` en clair dans
      la colonne du flux source. La valeur était juste, illisible, et le `source` non vide
      faisait disparaître le constat « Aucune caractéristique technique ».
    */
    if ('attributeName' in valeur) {
      const corps = [valeur.attributeName, valeur.attributeValue]
        .filter(Boolean).join(' : ');
      return valeur.sectionName ? `${valeur.sectionName} · ${corps}` : corps;
    }
    /*
      Une question et sa réponse.

      ⚠️ **La question d'abord, quoi que dise l'objet.** Les clés arrivent dans l'ordre
      `answer, question` — ce n'est ni le modèle ni nous : Prisma mappe `Json` sur **`jsonb`**,
      qui range les clés par longueur puis par octet, et « answer » fait six caractères quand
      « question » en fait huit. Vérifié en base. Inverser la déclaration de l'attribut n'y
      changerait rien ; seul l'affichage décide, et il lit les deux clés par leur nom.
    */
    if ('question' in valeur && 'answer' in valeur) {
      return [valeur.question, valeur.answer].filter(Boolean).join(' → ');
    }
    /*
      Une option de variante : {name, value}.

      ⚠️ Reconnue **après** le triplet technique, qui porte aussi un nom et une valeur sous
      d'autres clés. L'ordre des cas est donc du plus spécifique au plus général, et déplacer
      celui-ci plus haut ne casserait rien aujourd'hui — mais le jour où une forme portera
      `name` *et* `attributeName`, c'est la première reconnue qui gagnerait.
    */
    if ('name' in valeur && 'value' in valeur) {
      return [valeur.name, valeur.value].filter(Boolean).join(' : ');
    }
    /*
      Un produit apparenté : son identifiant, le type de cet identifiant, et la relation.

      La relation vient en tête parce que c'est elle qui se lit — « Accessoire », « Pièce
      compatible » —, l'identifiant ne parlant qu'à qui connaît le catalogue.
    */
    if ('id' in valeur && 'relationshipType' in valeur) {
      const ref = valeur.idType ? `${valeur.id} (${valeur.idType})` : valeur.id;
      return [valeur.relationshipType, ref].filter(Boolean).join(' · ');
    }
    /*
      La quantité vendue : {value, unit} → « 250 ml ».

      ⚠️ Reconnue **après** `{name, value}` : les deux portent `value`, et une option de
      variante prise pour une mesure s'afficherait « Marron undefined ». L'ordre des cas va du
      plus spécifique au plus général, et c'est `unit` qui tranche ici.
    */
    if ('value' in valeur && 'unit' in valeur) {
      return texteMesure(valeur) || JSON.stringify(valeur);
    }
    /*
      Garde-fou : une forme qu'on n'a pas prévue s'affiche brute plutôt que de faire tomber la
      fiche. Qui la voit sait qu'il manque un cas ici.

      ⚠️ **Et ce garde-fou se voit à l'écran** — c'est ainsi que les trois cas ci-dessus ont
      été trouvés, une capture montrant `{"answer":"…","question":"…"}` en clair dans la liste
      des suggestions. Les quatre formes de groupe du catalogue sont désormais couvertes ;
      `scripts/test-texte-attribut.js` refuse qu'une cinquième entre sans son cas.
    */
    return JSON.stringify(valeur);
  }

  if (nom === 'availability') {
    return LIBELLES_DISPONIBILITE[normaliserEnum(valeur)] || String(valeur);
  }

  return String(valeur);
};

/**
 * L'adresse d'une image, ramenée à la taille où on l'affiche.
 *
 * ## Le constat
 *
 * Les galeries du parc sont servies en pleine définition — 2 560 × 3 316 px, 353 Ko. Affichées
 * dans une vignette de 150 px, elles passent par un downscale de 17× que le navigateur fait
 * grossièrement : le résultat paraît pixellisé, alors que la source est excellente. Et cinq
 * vignettes font 1,7 Mo téléchargés pour une modale.
 *
 * Mesuré sur la même image Demandware :
 *
 * | Adresse | Rendu | Poids |
 * |---|---|---|
 * | sans paramètre | 2 560 × 3 316 | 353 Ko |
 * | `?sw=600` | 600 × 777 | **22 Ko** |
 *
 * ## ⚠️ Seulement pour l'affichage, et seulement là où la plateforme le permet
 *
 * **La valeur enregistrée n'est jamais touchée** : c'est l'adresse du marchand qui part dans le
 * flux, pas une variante de notre fabrication. Cette fonction ne sert que le `src` d'une
 * vignette.
 *
 * Et elle ne reconnaît qu'une plateforme : Demandware, qui sert **54 % des images du parc**
 * (31 592 sur 58 530) et documente son paramètre `sw`. Les autres hôtes — S3, CDN maison —
 * n'ont pas de redimensionnement à l'adresse, et leurs URL traversent **intactes** : inventer
 * un paramètre qu'un serveur ignore vaut mieux qu'inventer un paramètre qu'il refuse, mais ne
 * rien inventer vaut mieux que les deux.
 */
function vignetteDe(url, largeur = 400) {
  if (typeof url !== 'string' || !url.trim()) return url;
  // Demandware : `/dw/image/v2/` dans le chemin, paramètre `sw` pour la largeur servie.
  if (!/\/dw\/image\/v2\//.test(url)) return url;
  // Une largeur déjà demandée est respectée : le marchand a peut-être ses raisons.
  if (/[?&]sw=/.test(url)) return url;
  const n = Number(largeur);
  if (!Number.isInteger(n) || n < 50 || n > 4000) return url;
  return url + (url.includes('?') ? '&' : '?') + `sw=${n}`;
}

export {
  UNITES_MESURE,
  lireMesureSaisie,
  texteMesure,
  LIBELLES_DISPONIBILITE,
  estAdresse,
  nomDeFichier,
  normaliserEnum,
  formaterPrix,
  texteAttribut,
  vignetteDe
};
